import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { languageApi } from '../lib/languageApi';
import { kn } from '../i18n/kn';
import { hi } from '../i18n/hi';

/* ============================================================================
   THE UI TRANSLATION LAYER

   Deliberately built, not imported. It's ~a screenful of code, adds zero
   dependencies, and — the reason it's bespoke — it reads the SAME preferred
   language the notification layer uses (/api/me/language) and can fall back to
   the SAME server-side translation gateway for strings we haven't hand-authored
   yet. So the app is never half-translated: hand-written Kannada for the common
   parent strings (instant, correct), AI-translated + cached for the long tail.

   English is the base. t('...') takes the ENGLISH string as the key, so:
     - every call site stays readable (you see the real English inline),
     - a missing translation degrades to correct English, never a blank key.
   ========================================================================== */

type Lang = string; // BCP-47 short code: 'en', 'kn', …

interface I18nCtx {
  lang: Lang;
  setLang: (code: Lang) => Promise<void>;
  t: (english: string) => string;
  ready: boolean;
}

const Ctx = createContext<I18nCtx | null>(null);

// Bundled dictionaries. English is implicit (the key IS the English). Add a language by adding a map.
// Hand-authored dictionaries. Anything not here falls back to server AI translation (cached),
// so adding a language is additive: ship a file, add it here, and the rest fills in on demand.
const BUNDLED: Record<string, Record<string, string>> = { kn, hi };

// Session cache of AI-translated strings, so we ask the server once per string per language.
// Persisted to localStorage so it survives reloads (it's just UI copy, not sensitive).
const LS_KEY = 'eduvora.i18n.cache.v1';
function loadCache(): Record<string, Record<string, string>> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}
function saveCache(c: Record<string, Record<string, string>>) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(c)); } catch { /* quota — ignore */ }
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>('en');
  const [ready, setReady] = useState(false);
  const [, force] = useState(0);
  const aiCache = useRef<Record<string, Record<string, string>>>(loadCache());
  const pending = useRef<Set<string>>(new Set());

  // Load the user's saved language on mount.
  useEffect(() => {
    languageApi.get()
      .then((r) => setLangState(r.code || 'en'))
      .catch(() => setLangState('en'))
      .finally(() => setReady(true));
  }, []);

  const setLang = useCallback(async (code: Lang) => {
    setLangState(code);
    try { await languageApi.set(code); } catch { /* keep optimistic */ }
  }, []);

  // Ask the server to translate a string we don't have, then cache + re-render.
  const requestAi = useCallback((english: string, target: Lang) => {
    const key = `${target}::${english}`;
    if (pending.current.has(key)) return;
    pending.current.add(key);
    api.post<{ text: string }>('/api/i18n/translate', { text: english, lang: target })
      .then((r) => {
        (aiCache.current[target] ??= {})[english] = r.text || english;
        saveCache(aiCache.current);
        pending.current.delete(key);
        force((n) => n + 1);   // a batch re-render; cheap
      })
      .catch(() => { pending.current.delete(key); });
  }, []);

  const t = useCallback((english: string): string => {
    if (lang === 'en' || !english) return english;
    const bundled = BUNDLED[lang]?.[english];
    if (bundled) return bundled;
    const cached = aiCache.current[lang]?.[english];
    if (cached) return cached;
    // Not known yet: kick off a translation and show English until it lands.
    requestAi(english, lang);
    return english;
  }, [lang, requestAi]);

  return <Ctx.Provider value={{ lang, setLang, t, ready }}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const c = useContext(Ctx);
  if (!c) {
    // Safe fallback if used outside the provider (e.g. platform console): identity English.
    return { lang: 'en', setLang: async () => {}, t: (s) => s, ready: true };
  }
  return c;
}

/** Convenience hook for the common case: `const t = useT();  <h1>{t('Fees')}</h1>` */
export function useT(): (english: string) => string {
  return useI18n().t;
}
